import React from 'react';
import { Formik } from 'formik';
import { Payment, CashRewardInfo, CashRewardType, RewardType, PhysicalRewardInfo } from '@luckycart/xpn-types';
import parse from 'html-react-parser';

import { PrivacyInformations } from './PrivacyInformations';
import { ProvidedQueryParams } from '../../controllers/Reward.experience';
import { eventServiceInstance } from '../../services/shopper.event.service';
import { Button, StyledButtonSpan } from '../reward-types/common/common.style';
import { CashRewardForm } from './CashRewardForm';
import { PhysicalRewardForm } from './PhysicalRewardForm';

import LockIcon from './images/lock.svg';
import { StyledForm } from './RewardForm.style';
import { PhysicalOlympicGamesRewardForm } from './PhysicalOlympicGamesRewardForm';

const carrefourOlympicGamesOperationId = '64a5706e03f76b2b1d2a2fd1';
const mirrorCarrefourOlympicGamesOperationId = '64a5707203f76b24ba2a2fd2';

export interface RewardFormValues {
  firstName: string;
  lastName: string;
  iban: string;
  bic: string;
  email: string;
  phone: string;
  address: string;
  additionalAddress: string;
  zipCode: string;
  city: string;
  recipients?: {
    firstName: string;
    lastName: string;
  }[]
}

interface Props {
  config: Payment;
  queryParams: ProvidedQueryParams;
  setIsFilled: (value: boolean)=> void;
  rewardType: RewardType | null;
}

export const RewardForm: React.FunctionComponent<Props> = ({
  config,
  queryParams: { siteKey, shopperId, experienceId, operationId },
  setIsFilled,
  rewardType,
}: Props) => {

  const [isBank, setIsBank] = React.useState<boolean>(true);
  const { forms } = config;

  const validateRequired = (value: string): string | undefined => {
    return value
      ? undefined
      : 'requis';
  };

  const getRewardInfo = (values: RewardFormValues): CashRewardInfo | PhysicalRewardInfo | null => {
    const { firstName, lastName, iban, bic, email, phone, address, additionalAddress, zipCode, city, recipients } = values;
    const formattedIban = iban.replace(/\s/gu, '');
    const formattedBic = bic.slice(0, 8);
    switch (rewardType) {
    case RewardType.CASH:
      return isBank
        ? {
          type: CashRewardType.BANK,
          firstname: firstName,
          lastname: lastName,
          bank: {
            iban: formattedIban,
            bic: formattedBic,
          },
        }
        : {
          type: CashRewardType.PAYPAL,
          firstname: firstName,
          lastname: lastName,
          paypal: {
            address: email,
          },
        };
    case RewardType.PHYSICAL: {
      const rewardInfo: PhysicalRewardInfo = {
        firstname: firstName,
        lastname: lastName,
        phone,
        address,
        additionalAddress,
        zipCode,
        city,
      };
      if (Array.isArray(recipients) && recipients.length > 0) {
        rewardInfo.recipients = recipients;
      }
      if (typeof email === 'string' && email.length > 0) {
        rewardInfo.email = values.email;
      }
      return rewardInfo;
    }
    default:
      return null;
    }
  };

  const initialValues: RewardFormValues = {
    firstName: '',
    lastName: '',
    iban: '',
    bic: '',
    email: '',
    phone: '',
    address: '',
    additionalAddress: '',
    zipCode: '',
    city: '',
    recipients: [],
  };

  return (
    <Formik
      initialValues={initialValues}
      validateOnChange={false}
      onSubmit={async (values) => {
        if (rewardType === null) {
          return;
        }
        const rewardInfo = getRewardInfo(values);
        if (rewardInfo !== null) {
          const isSent = await eventServiceInstance.sendRewardInfoFilledEvent(siteKey, shopperId, experienceId, rewardInfo, rewardType);
          setIsFilled(isSent);
        }
      }}>
      { (formikProps) => <StyledForm>
        {rewardType === RewardType.CASH && <CashRewardForm
          formikProps={formikProps}
          config={config}
          isBank={isBank}
          setIsBank={setIsBank}
          validateRequired={validateRequired}
        />}
        {[mirrorCarrefourOlympicGamesOperationId, carrefourOlympicGamesOperationId].includes(operationId) &&
            <PhysicalOlympicGamesRewardForm validateRequired={validateRequired} formikProps={formikProps}/>}
        {rewardType === RewardType.PHYSICAL &&
        ![mirrorCarrefourOlympicGamesOperationId, carrefourOlympicGamesOperationId].includes(operationId) &&
        <PhysicalRewardForm validateRequired={validateRequired} />}
        <Button type="submit">
          <img src={LockIcon} alt="lockIcon" />
          <StyledButtonSpan>Valider</StyledButtonSpan>
        </Button>
        <PrivacyInformations text={parse(forms.desc ?? '')}/>
      </StyledForm>
      }
    </Formik>
  );
};
